import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-10b86a9b"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "content" }
const _hoisted_2 = { class: "setting-body" }
const _hoisted_3 = { class: "setting-menu" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "setting-content" }
const _hoisted_6 = { class: "footer-setting" }

import DeviceSetting from '@/components/checkup/DeviceSetting.vue'
import BackgroundSetting from '@/components/checkup/BackgroundSetting.vue'
import VideoQualitySetting from '@/components/checkup/VideoQualitySetting.vue'
import { computed, ref, PropType } from 'vue'
import { useBackgroundStore, useDeviceStore } from '@/stores'
import { getDeviceEnableState } from '@/services/device-state'
import useSpaceApi from '../../composables/api/useSpaceApi'
import { Engine } from '../../types/enums/room.enum'
import { covertVideoBgToBackgroundType } from '../../helpers/background.helper'
import { currentResolution } from '@/composables/twilio/useTwilioState'

interface DeviceSettingPopupEmits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:currentTab', value: string): void
  (e: 'update-device'): void
  (e: 'cancel'): void
}
type Option = 'video' | 'quality' | 'setting'
interface IMenus {
  name: string
  icon: string
  value: string
  isBeta?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DeviceSettingPopup',
  props: {
  modelValue: {
    type: Boolean,
    default: false,
  },
  currentTab: {
    type: String,
    default: 'setting',
  },
  engine: {
    type: String as PropType<Engine>,
    default: Engine.TWILIO,
  },
  option: {
    type: Array as PropType<Option[]>,
    default() {
      return ['setting', 'video', 'quality']
    },
  },
},
  emits: ["update:modelValue", "update:currentTab", "update-device", "cancel"],
  setup(__props, { emit: __emit }) {

// DeviceSettingPopup Component
const props = __props

const emit = __emit

/**
 * Api
 */
const { saveLogsChangeBackground, saveLogsChangeResolution } = useSpaceApi()

/**
 * Store
 */
const background = useBackgroundStore()
const device = useDeviceStore()

/**
 * State
 */
const nextTab = ref('')
const enableChanged = ref<boolean>(false)
const isOpenConfirm = ref<boolean>(false)

/**
 * Computed
 */

const menus = computed<IMenus[]>(() => {
  const list: IMenus[] = []
  if (props.option.includes('setting'))
    list.push({ name: 'Mic & Camera', icon: 'setting', value: 'setting', isBeta: false })
  if (device.canChangeBackground && props.option.includes('video')) {
    list.push({ name: 'Video Effect', icon: 'effect', value: 'video', isBeta: true })
  }
  if (props.engine === Engine.TWILIO && props.option.includes('quality')) {
    list.push({
      name: 'Video Quality',
      icon: 'video-quality',
      value: 'quality',
      isBeta: true,
    })
  }
  return list
})
const deviceChanged = computed(
  () =>
    device.audioChanged ||
    device.speakerChanged ||
    device.videoChanged ||
    device.cameraOnChanged ||
    device.microphoneOnChanged,
)
const effectChanged = computed(() => background.backgroundChanged)
const qualityChanged = computed(() => device.videoQualityChanged)
const content = computed(() =>
  nextTab.value ? 'Confirm your change before switch new tab' : 'Confirm your change before close',
)
const isChanged = computed(() =>
  props.currentTab === 'setting'
    ? deviceChanged.value
    : props.currentTab === 'video'
    ? effectChanged.value || device.cameraOnChanged
    : props.currentTab === 'quality'
    ? qualityChanged.value
    : false,
)

const changeMenu = (value?: string) => {
  if (!value) {
    toggle()
    return
  }
  if (props.currentTab === value) return
  if (isChanged.value || enableChanged.value) {
    nextTab.value = value
    isOpenConfirm.value = true
    return
  }
  emit('update:currentTab', value)
  nextTab.value = ''
}

const saveChanged = () => {
  switch (props.currentTab) {
    case 'setting': {
      device.setVideoDevice(device.maskSelectedVideo)
      device.setMicrophoneDevice(device.maskSelectedAudio)
      device.setSpeakerDevice(device.maskSelectedSpeaker)
      device.cameraOn = device.maskCameraOn
      device.microphoneOn = device.maskMicrophoneOn
      device.setStateEnableToLocal()
      break
    }
    case 'video': {
      if (background.backgroundChanged) {
        background.currentBackground = background.selectedBackground
        background.setStateBackground(background.currentBackground)
        // Log Change Background
        const { backgroundType, imagePath } = covertVideoBgToBackgroundType(
          background.currentBackground,
        )
        saveLogsChangeBackground({ engine: props.engine, backgroundType, imagePath })
      }
      device.cameraOn = device.maskCameraOn
      device.setStateEnableToLocal()
      break
    }
    case 'quality': {
      device.setCurrentVideoQuality()
      saveLogsChangeResolution(currentResolution.value)
      break
    }
  }
}

const discardChanged = () => {
  const localData = getDeviceEnableState()
  device.maskCameraOn = device.cameraOn = localData.video
  switch (props.currentTab) {
    case 'setting': {
      device.maskSelectedAudio = device.selectedAudio
      device.maskSelectedSpeaker = device.selectedSpeaker
      device.maskSelectedVideo = device.selectedVideo
      device.maskMicrophoneOn = device.microphoneOn = localData.audio
      break
    }
    case 'video': {
      background.selectedBackground = background.currentBackground
      break
    }
    case 'quality': {
      device.selectedVideoQuality = device.currentVideoQuality
      device.resolution = currentResolution.value
      break
    }
  }
}

const toggle = () => {
  emit('update:modelValue', !props.modelValue)
}

const applyDevice = async () => {
  saveChanged()
  toggle()
  emit('update-device')
}
const cancel = () => {
  if (isChanged.value || enableChanged.value) {
    isOpenConfirm.value = true
    return
  }
  discardChanged()
  toggle()
  emit('cancel')
}

const cancelLatestChange = () => {
  discardChanged()
  isOpenConfirm.value = false
  changeMenu(nextTab.value)
}

const saveLatestChange = () => {
  saveChanged()
  isOpenConfirm.value = false
  emit('update-device')
  changeMenu(nextTab.value)
}

return (_ctx: any,_cache: any) => {
  const _component_Popup = _resolveComponent("Popup")!
  const _component_SvgIcon = _resolveComponent("SvgIcon")!
  const _component_BetaTag = _resolveComponent("BetaTag")!

  return (_openBlock(), _createBlock(_component_Popup, {
    title: "Device Setting",
    width: "640",
    open: __props.modelValue,
    class: "device-setting-popup",
    onClose: cancel
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_6, [
        (isChanged.value || enableChanged.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("button", {
                type: "button",
                class: "btn btn-tertiary",
                onClick: cancel
              }, "Cancel"),
              _createElementVNode("button", {
                type: "button",
                class: "btn btn-primary",
                onClick: applyDevice
              }, "Save")
            ], 64))
          : (_openBlock(), _createElementBlock("button", {
              key: 1,
              type: "button",
              class: "btn btn-tertiary",
              onClick: cancel
            }, "Close"))
      ])
    ]),
    default: _withCtx(() => [
      _createVNode(_component_Popup, {
        title: "Confirm your change",
        open: isOpenConfirm.value,
        class: "confirm-setting-changed",
        "can-close": false
      }, {
        footer: _withCtx(() => [
          _createElementVNode("div", { class: "footer-setting" }, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-tertiary",
              onClick: cancelLatestChange
            }, "Cancel"),
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-primary",
              onClick: saveLatestChange
            }, "Confirm")
          ])
        ]),
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, _toDisplayString(content.value), 1)
        ]),
        _: 1
      }, 8, ["open"]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(menus.value, (menu, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `menu-${index}`,
              class: _normalizeClass(["menu flex items-center", { active: menu.value === __props.currentTab }]),
              onClick: ($event: any) => (changeMenu(menu.value))
            }, [
              _createVNode(_component_SvgIcon, {
                icon: menu.icon,
                "inherit-fill": true,
                height: "20",
                class: "mr-1"
              }, null, 8, ["icon"]),
              _createElementVNode("span", null, _toDisplayString(menu.name), 1),
              (menu.isBeta)
                ? (_openBlock(), _createBlock(_component_BetaTag, {
                    key: 0,
                    class: "ml-auto"
                  }))
                : _createCommentVNode("", true)
            ], 10, _hoisted_4))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_5, [
          (__props.currentTab === 'video')
            ? (_openBlock(), _createBlock(BackgroundSetting, {
                key: 0,
                engine: __props.engine
              }, null, 8, ["engine"]))
            : _createCommentVNode("", true),
          (__props.currentTab === 'setting')
            ? (_openBlock(), _createBlock(DeviceSetting, {
                key: 1,
                engine: __props.engine
              }, null, 8, ["engine"]))
            : _createCommentVNode("", true),
          (__props.currentTab === 'quality')
            ? (_openBlock(), _createBlock(VideoQualitySetting, { key: 2 }))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})